import {Button} from '@/components/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/dropdown';
import {DataTable} from '@/components/table/datatable';
import {idrFormatter} from '@/lib/utils/currency';
import {
    NoSymbolIcon,
    PencilSquareIcon,
    PlusIcon,
    TrashIcon,
} from '@heroicons/react/24/outline';
import {ClipboardDocumentIcon, DocumentCheckIcon, EllipsisHorizontalIcon, EyeIcon} from '@heroicons/react/24/solid';
import {ColumnDef} from '@tanstack/react-table';
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {AppDispatch} from "@/root/store";
import {useDispatch, useSelector} from "react-redux";
import React, {useEffect, useState} from "react";
import {DeleteAffiliator, GetAffiliators, StoreAffiliator} from "@/services/affiliators";
import {ShowEvent} from "@/services/events";
import {clearErrors} from "@/reducers/affiliators";
import {FormLabel} from "@/components/form/formlabel";
import {Input} from "@/components/form/input";
import {Modal} from "@/components/modal";
import toast from "react-hot-toast";
import {DeleteAnnouncement, GetAnnouncement, StoreAnnouncement} from "@/services/announcements";
import {Textarea} from "@/components/form/textarea";
import {Checkbox} from "@/components/form/checkbox";

interface Outbox {
    id: number;
    uuid: string;
    subject: string;
    body: string;
    number_recipients: string;
    number_emails_processed: number;
}

export const defaultData = {subject: '', body: '', type: 'announcement', channel: 'email', recipient: [
        {
            type: "all",
            model: "Ticket",
            value: null
        }
    ]};

export function OrgEventAnnouncement() {
    const location = useLocation();
    const navigate = useNavigate();
    const {slug} = useParams();
    const dispatch: AppDispatch = useDispatch();
    const [page, setPage] = useState(1);
    const [showModal, setShowModal] = useState(false);
    const [showDelete, setShowDelete] = useState({
        modal: false,
        id: 0
    });
    const [formData, setFormData] = useState(defaultData);

    const {
        announcements,
        announcement,
        announcementDeleted,
        paginator,
        errors
    } = useSelector((state: any) => state.announcements);

    const {event} = useSelector((state: any) => state.event);

    const queryParams = new URLSearchParams(location.search);
    const searchQuery: string = queryParams.get('search') ?? '';

    useEffect(() => {
        if (announcement.id) {
            setShowModal(false)
        }

        dispatch(GetAnnouncement(slug, searchQuery, page))
    }, [searchQuery, page, announcement, announcementDeleted]);

    useEffect(() => {
        dispatch(ShowEvent(slug))
    }, [slug]);

    const setData = (e: any, element: string) => {
        setFormData({...formData, [element]: e.target.value})

        dispatch(clearErrors());
    }

    const submitAnnouncement = () => {
        dispatch(StoreAnnouncement(event.id, formData))
    }

    const deleteAnnouncement = (announcementId: number) => {
        dispatch(DeleteAnnouncement(event.id, announcementId))

        setShowDelete({modal: false, id: 0})
    }

    const ctaButton = <Button onClick={() => {
        setShowModal(true)
        setFormData(defaultData)
    }} leftIcon={<PlusIcon/>}>Buat pengumuman</Button>;

    const columns: ColumnDef<Outbox>[] = [
        {
            header: 'Subject',
            cell: ({row}) => {
                const subject = row.original.subject;
                return (
                    <div>
                        <p>{subject}</p>
                    </div>
                );
            },
        },
        {
            header: 'Isi Email',
            accessorKey: 'body',
        },
        {
            header: 'Penerima',
            cell: ({row}) => {
                const recipients = row.original.number_recipients;
                const processed = row.original.number_emails_processed;
                return (
                    <div>
                        <span className={'text-red-500'}>{processed}/</span>{recipients}
                    </div>
                );
            },
        },
        {
            header: 'Aksi',
            cell: ({row}) => {
                return (
                    <DropdownMenu>
                        <DropdownMenuTrigger>
                            <Button
                                size='sm'
                                variant='neutral'
                                leftIcon={<EllipsisHorizontalIcon/>}
                            />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent>
                            <DropdownMenuItem onClick={() => {
                                navigate(`${row.original.uuid}/recipients`)
                            }}>
                                <EyeIcon className='mr-2 h-4 w-4'/>
                                <span>Lihat Detail</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => {
                                setShowDelete({modal: true, id: row.original.id})
                            }}>
                                <TrashIcon className='mr-2 h-4 w-4 text-danger'/>
                                <span className='text-danger'>Hapus</span>
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                );
            },
        },
    ];

    return (
        <>
            <Modal
                show={showModal}
                onRequestClose={() => setShowModal(false)}
                width='medium'
                title='Buat Pengumuman'
                body={
                    <div className='flex flex-col gap-4'>
                        <div className="mb-3">
                            <FormLabel label='Subject' errorText={errors.subject} required>
                                <Input type='text' value={formData.subject} onChange={(e) => setData(e, 'subject')}/>
                            </FormLabel>
                        </div>

                        <FormLabel label='Penerima' errorText={errors.recipient}>
                            <div className="mb-3">
                                <FormLabel label={`Semua pemegang tiket`} errorText={errors.recipient}
                                           inline>
                                    <Checkbox checked readOnly disabled />
                                </FormLabel>
                            </div>
                        </FormLabel>

                        <div className="mb-3">
                            <FormLabel label='Isi Email' errorText={errors.body} required>
                                <Textarea onChange={(e) => setData(e, 'body')}>
                                    {formData.body}
                                </Textarea>
                            </FormLabel>
                        </div>
                    </div>
                }
                footer={
                    <div className='flex justify-end gap-3'>
                        <Button
                            variant='neutral'
                            onClick={() => setShowModal(false)}
                            leftIcon={<NoSymbolIcon/>}
                        >
                            Batal
                        </Button>
                        <Button
                            onClick={submitAnnouncement}
                            leftIcon={<DocumentCheckIcon/>}
                        >
                            Simpan
                        </Button>
                    </div>
                }
            />

            <Modal
                show={showDelete.modal}
                onRequestClose={() => setShowDelete({...showDelete, modal: false})}
                width='medium'
                title='Hapus Pengumuman'
                body={
                    <>
                        <p>Apakah kamu yakin ingin menghapus pengumuman ini?</p>
                    </>
                }
                footer={
                    <div className='flex justify-end gap-3'>
                        <Button
                            variant='neutral'
                            onClick={() => setShowDelete({...showDelete, modal: false})}
                            leftIcon={<NoSymbolIcon/>}
                        >
                            Batal
                        </Button>
                        <Button
                            variant={'danger'}
                            onClick={() => {
                                deleteAnnouncement(showDelete.id)
                            }
                            }
                            leftIcon={<TrashIcon/>}
                        >
                            Ya, Hapus
                        </Button>
                    </div>
                }
            />

            <DataTable
                name='Daftar pengumuman'
                noun={'pengumuman'}
                ctaButton={!['finance', 'scanner'].includes(
                  event?.logged_in_organizer?.role
                ) ? ctaButton : <></>}
                columns={columns}
                data={announcements}
                total={paginator.to}
                currentTotal={paginator.total}
                currentPage={paginator.current_page}
                pages={paginator.last_page}
                onPageRequest={(nextPage) => setPage(nextPage)}
            />
        </>
    );
}
